import {Hash} from "../../../interfaces/hashInterfaces";
import {IRoleData, IRoleMetadata} from "../../../interfaces/roleInterfaces";

export enum DocumentRoleCapability {
  edit = 1, // edit folders & non-modeling
  moveArchive = 2,
  manageSharesAndPermissions = 3,
  publishUnpublish = 4,
  viewOnSubmissionsDashboard = 5,
  assignGroupMembersToWorkflowSteps = 6,
  viewSubmissionDetailsAndExport = 7,
  terminateSubmissionWorkflowSteps = 8,
  viewAllLicenseHistories = 9,
  viewLicenseeHistory = 10,
  viewComplaints = 11,
  manageLicenseStatuses = 12,
  reassignCompletersForWorkflowSteps = 13,
  inputExternalSubmissionData = 14,
  viewAllSubmissionDetails = 15,
  viewObjectModelRecordsOnSubmissionDashboard = 16,
  exportObjectModelRecordData = 17,
  editAnalytic = 18,
  moveArchiveAnalytic = 19,
  publishUnpublishAnalytic = 20,
  viewAnalytic = 21,
  exportAnalyticData = 22,
  editAllSubmissionDetails = 23,
  debugProcessFlows = 24,
  deleteSubmissions = 25,
  downloadApp = 26,
  uploadApp = 27,
  editModeling = 28,
  createDocumentsFolders = 29,
  createModeling = 30,
  createAppGroups = 31,
  renameAppGroups = 32,
  transferAppGroupOwner = 33,
  inviteUsersToAppGroups = 34,
  assignAppGroupRoles = 35,
  deleteAppGroups = 36,
  removeUsersFromAppGroups = 37,
  transferAppOwner,
  exportAppPermissions = 39,
}

export const DocumentRoleMetadata: { [key in DocumentRoleCapability]: IRoleMetadata } = {
  [DocumentRoleCapability.createDocumentsFolders]: {
    label: 'Create documents & folders',
  },
  [DocumentRoleCapability.createModeling]: {
    label: 'Create models, views, mappings & process flows',
  },
  [DocumentRoleCapability.edit]: {
    label: 'Edit documents & folders',
  },
  [DocumentRoleCapability.editModeling]: {
    label: 'Edit models, views, mappings & process flows',
  },
  [DocumentRoleCapability.moveArchive]: {
    label: 'Move/archive',
  },
  [DocumentRoleCapability.publishUnpublish]: {
    label: 'Publish/unpublish document',
  },
  [DocumentRoleCapability.manageSharesAndPermissions]: {
    label: 'Manage shares & permissions',
  },
  [DocumentRoleCapability.viewAllLicenseHistories]: {
    label: 'View license histories',
  },
  [DocumentRoleCapability.viewLicenseeHistory]: {
    label: 'View licensee history',
  },
  [DocumentRoleCapability.viewComplaints]: {
    label: 'View complaints',
  },
  [DocumentRoleCapability.manageLicenseStatuses]: {
    label: 'Manage license statuses',
  },
  [DocumentRoleCapability.viewOnSubmissionsDashboard]: {
    label: 'View document on Reporting & Submissions dashboard',
  },
  [DocumentRoleCapability.viewSubmissionDetailsAndExport]: {
    label: 'View all submission details & export data',
  },
  [DocumentRoleCapability.assignGroupMembersToWorkflowSteps]: {
    label: 'Assign group completers for submission',
  },
  [DocumentRoleCapability.terminateSubmissionWorkflowSteps]: {
    label: 'Terminate submission workflow steps',
  },
  [DocumentRoleCapability.reassignCompletersForWorkflowSteps]: {
    label: 'Reassign completer assignments for submission'
  },
  [DocumentRoleCapability.inputExternalSubmissionData]: {
    label: 'Input external data'
  },
  [DocumentRoleCapability.viewAllSubmissionDetails]: {
    label: 'View all submission details',
  },
  [DocumentRoleCapability.viewObjectModelRecordsOnSubmissionDashboard]: {
    label: "View records on Reporting & Submissions"
  },
  [DocumentRoleCapability.exportObjectModelRecordData]: {
    label: "Export Data"
  },
  [DocumentRoleCapability.editAnalytic]: {
    label: "Edit analytic"
  },
  [DocumentRoleCapability.moveArchiveAnalytic]: {
    label: "Move/archive analytic"
  },
  [DocumentRoleCapability.publishUnpublishAnalytic]: {
    label: "Publish/unpublish analytic"
  },
  [DocumentRoleCapability.viewAnalytic]: {
    label: "View analytic"
  },
  [DocumentRoleCapability.exportAnalyticData]: {
    label: "Export analytic data"
  },
  [DocumentRoleCapability.editAllSubmissionDetails]: {
    label: 'Edit all submission details',
  },
  [DocumentRoleCapability.debugProcessFlows]: {
    label: 'Run Debug'
  },
  [DocumentRoleCapability.deleteSubmissions]: {
    label: 'Delete submissions'
  },
  [DocumentRoleCapability.downloadApp]: {
    label: 'Download App'
  },
  [DocumentRoleCapability.uploadApp]: {
    label: 'Upload to App'
  },
  [DocumentRoleCapability.createAppGroups]: {
    label: 'Create app groups'
  },
  [DocumentRoleCapability.renameAppGroups]: {
    label: 'Rename app groups'
  },
  [DocumentRoleCapability.transferAppGroupOwner]: {
    label: 'Transfer app group owner'
  },
  [DocumentRoleCapability.deleteAppGroups]: {
    label: 'Delete app groups'
  },
  [DocumentRoleCapability.inviteUsersToAppGroups]: {
    label: 'Invite users to app groups'
  },
  [DocumentRoleCapability.assignAppGroupRoles]: {
    label: 'Assign app groups roles'
  },
  [DocumentRoleCapability.removeUsersFromAppGroups]: {
    label: 'Remove users from app groups & app'
  },
  [DocumentRoleCapability.transferAppOwner]: {
    label: 'Transfer app owner'
  },
  [DocumentRoleCapability.exportAppPermissions]: {
    label: 'Export app permissions'
  },
};

export const DocumentRoleData: Hash<Hash<IRoleData<DocumentRoleCapability>[]>> = {
  'General Settings': {
    'Manage App': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.downloadApp].label,
        capability: DocumentRoleCapability.downloadApp
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.uploadApp].label,
        capability: DocumentRoleCapability.uploadApp
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.createAppGroups].label,
        capability: DocumentRoleCapability.createAppGroups
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.renameAppGroups].label,
        capability: DocumentRoleCapability.renameAppGroups
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.transferAppGroupOwner].label,
        capability: DocumentRoleCapability.transferAppGroupOwner
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.deleteAppGroups].label,
        capability: DocumentRoleCapability.deleteAppGroups
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.inviteUsersToAppGroups].label,
        capability: DocumentRoleCapability.inviteUsersToAppGroups
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.assignAppGroupRoles].label,
        capability: DocumentRoleCapability.assignAppGroupRoles
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.removeUsersFromAppGroups].label,
        capability: DocumentRoleCapability.removeUsersFromAppGroups
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.transferAppOwner].label,
        capability: DocumentRoleCapability.transferAppOwner
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.exportAppPermissions].label,
        capability: DocumentRoleCapability.exportAppPermissions
      },
    ],
    'Manage Documents & Folders': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.createDocumentsFolders].label,
        capability: DocumentRoleCapability.createDocumentsFolders
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.createModeling].label,
        capability: DocumentRoleCapability.createModeling,
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.edit].label,
        capability: DocumentRoleCapability.edit
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.editModeling].label,
        capability: DocumentRoleCapability.editModeling,
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.moveArchive].label,
        capability: DocumentRoleCapability.moveArchive
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.publishUnpublish].label,
        capability: DocumentRoleCapability.publishUnpublish
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.manageSharesAndPermissions].label,
        capability: DocumentRoleCapability.manageSharesAndPermissions
      },
    ],
    'Manage Licenses': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewAllLicenseHistories].label,
        capability: DocumentRoleCapability.viewAllLicenseHistories
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewLicenseeHistory].label,
        capability: DocumentRoleCapability.viewLicenseeHistory
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewComplaints].label,
        capability: DocumentRoleCapability.viewComplaints
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.manageLicenseStatuses].label,
        capability: DocumentRoleCapability.manageLicenseStatuses
      },
    ],
    'Manage Submissions': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewOnSubmissionsDashboard].label,
        capability: DocumentRoleCapability.viewOnSubmissionsDashboard
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewAllSubmissionDetails].label,
        capability: DocumentRoleCapability.viewAllSubmissionDetails
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewSubmissionDetailsAndExport].label,
        capability: DocumentRoleCapability.viewSubmissionDetailsAndExport
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.editAllSubmissionDetails].label,
        capability: DocumentRoleCapability.editAllSubmissionDetails
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.inputExternalSubmissionData].label,
        capability: DocumentRoleCapability.inputExternalSubmissionData
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.assignGroupMembersToWorkflowSteps].label,
        capability: DocumentRoleCapability.assignGroupMembersToWorkflowSteps
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.reassignCompletersForWorkflowSteps].label,
        capability: DocumentRoleCapability.reassignCompletersForWorkflowSteps
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.terminateSubmissionWorkflowSteps].label,
        capability: DocumentRoleCapability.terminateSubmissionWorkflowSteps
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.deleteSubmissions].label,
        capability: DocumentRoleCapability.deleteSubmissions
      }
    ],
    'Manage Object Models': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewObjectModelRecordsOnSubmissionDashboard].label,
        capability: DocumentRoleCapability.viewObjectModelRecordsOnSubmissionDashboard
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.exportObjectModelRecordData].label,
        capability: DocumentRoleCapability.exportObjectModelRecordData
      },
    ],
    'Manage Analytics': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.editAnalytic].label,
        capability: DocumentRoleCapability.editAnalytic
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.moveArchiveAnalytic].label,
        capability: DocumentRoleCapability.moveArchiveAnalytic
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.publishUnpublishAnalytic].label,
        capability: DocumentRoleCapability.publishUnpublishAnalytic
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.viewAnalytic].label,
        capability: DocumentRoleCapability.viewAnalytic
      },
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.exportAnalyticData].label,
        capability: DocumentRoleCapability.exportAnalyticData
      }
    ],
    'Manage Process Flows': [
      {
        label: DocumentRoleMetadata[DocumentRoleCapability.debugProcessFlows].label,
        capability: DocumentRoleCapability.debugProcessFlows,
      },
    ]
  }
};

export let documentViewReportingDashboardCapabilities = [
  DocumentRoleCapability.viewOnSubmissionsDashboard,
  DocumentRoleCapability.viewSubmissionDetailsAndExport,
  DocumentRoleCapability.viewAllSubmissionDetails,
  DocumentRoleCapability.inputExternalSubmissionData
];

export const appGroupDocCapabilities = [
  DocumentRoleCapability.createAppGroups,
  DocumentRoleCapability.renameAppGroups,
  DocumentRoleCapability.transferAppGroupOwner,
  DocumentRoleCapability.deleteAppGroups,
  DocumentRoleCapability.inviteUsersToAppGroups,
  DocumentRoleCapability.assignAppGroupRoles,
  DocumentRoleCapability.removeUsersFromAppGroups,
  DocumentRoleCapability.exportAppPermissions,
  DocumentRoleCapability.transferAppOwner,
]
